<script setup lang="ts">
import type { StyleValue } from 'vue';
import { NuxtImg } from '#components';

interface IProps {
    src: string;
    width?: number;
    height?: number;
    alt?: string;
    borderRadius?: number | string;
    imageFit?: 'unset' | 'contain' | 'cover' | 'fill' | 'scale-down' | 'none';
    preload?: boolean;
    sizeUnit?: string;
}
const {
    imageFit = 'unset',
    alt,
    preload,
    src,
    height,
    width,
    borderRadius,
    sizeUnit = 'px',
} = defineProps<IProps>();
const emit = defineEmits<{
    loaded: [];
}>();
const root = ref<HTMLDivElement>();
const loaded = ref(preload);
const timeToLoad = ref(preload);
const imageStyles = computed<StyleValue>(() => ({
    'border-radius': borderRadius
        ? typeof borderRadius === 'number'
            ? `${borderRadius}px`
            : borderRadius
        : undefined,
    'object-fit': imageFit,
    height: height ? `${height}${sizeUnit}` : '100%',
    width: width ? `${width}${sizeUnit}` : '100%',
}));
const startObserving = () => {
    const observer = new IntersectionObserver(
        entries => {
            if (entries[0].isIntersecting) {
                timeToLoad.value = true;
                observer.disconnect();
            }
        },
        {
            threshold: [0, 0.2],
            root: null,
            rootMargin: '0px 0px 0px 0px',
        }
    );
    if (root.value) {
        observer.observe(root.value);
    }
};
const onLoad = () => {
    emit('loaded');
    loaded.value = true;
};
onMounted(() => {
    if (typeof IntersectionObserver === 'undefined') {
        timeToLoad.value = true;
    } else {
        startObserving();
    }
});
</script>

<template>
    <div ref="root" class="lazy-image-container">
        <NuxtImg
            v-if="timeToLoad && src"
            format="webp"
            :src
            :class="{ loaded }"
            :style="imageStyles"
            :alt
            :preload
            @load="onLoad"
        />
    </div>
</template>
<style lang="scss" scoped>
img {
    transition: opacity 300ms;
}

img:not(.loaded) {
    opacity: 0;
}

.loaded {
    opacity: 1;
}
</style>
